import { Link } from '@tanstack/react-router'
import { useAuth } from '@/common/hooks/useAuth'
import {
  useDashboardSummary,
  type DashboardTimesheetSummary,
  type DashboardPayPeriod,
  type PayPeriodState,
} from '@/common/hooks/useDashboard'
import { useNotifications } from '@/common/hooks/useNotifications'
import { NotificationSection } from '@/common/components/notification-section/NotificationSection'
import { Button } from '@/common/components/button'
import * as styles from './Dashboard.css'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPeriod(start: string, end: string) {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', fmt)}`
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function statusClassName(status: string) {
  switch (status) {
    case 'submitted':
      return styles.statusSubmitted
    case 'approved':
      return styles.statusApproved
    case 'rejected':
      return styles.statusRejected
    default:
      return styles.statusDraft
  }
}

function periodStateLabel(state: PayPeriodState | null): string {
  switch (state) {
    case 'active':
      return 'Submission Open'
    case 'closed':
      return 'Closed'
    case 'drafting':
      return 'In Progress'
    default:
      return ''
  }
}

function periodStateClassName(state: PayPeriodState | null): string {
  switch (state) {
    case 'active':
      return styles.stateActive
    case 'closed':
      return styles.stateClosed
    default:
      return styles.stateDrafting
  }
}

// ---------------------------------------------------------------------------
// Timesheet card
// ---------------------------------------------------------------------------

function TimesheetCard({
  ts,
  periodState,
}: {
  ts: DashboardTimesheetSummary
  periodState: PayPeriodState | null
}) {
  const isActionable = ts.status === 'draft' || ts.status === 'rejected'
  const ctaLabel =
    ts.status === 'rejected'
      ? 'Revise & Resubmit'
      : periodState === 'drafting'
        ? 'Enter Hours'
        : 'Complete & Submit'
  const stateLabel = periodStateLabel(periodState)

  return (
    <div className={`${styles.card} ${isActionable ? styles.cardActionable : ''}`}>
      <div className={styles.cardLeft}>
        <div className={styles.cardPeriodRow}>
          <span className={styles.cardPeriod}>
            {formatPeriod(ts.pay_period.start_date, ts.pay_period.end_date)}
          </span>
          {stateLabel && (
            <span className={periodStateClassName(periodState)}>{stateLabel}</span>
          )}
        </div>
        <span className={styles.cardHours}>
          {ts.total_hours}h logged
          {ts.total_standard_hours > 0 && ` · ${ts.total_standard_hours}h standard`}
          {ts.total_overtime_hours > 0 && ` · ${ts.total_overtime_hours}h overtime`}
        </span>
        {ts.status === 'rejected' && ts.rejection_reason && (
          <span className={styles.rejectionText}>Rejected: {ts.rejection_reason}</span>
        )}
      </div>
      <div className={styles.cardRight}>
        <span className={statusClassName(ts.status)}>{ts.status}</span>
        {isActionable && (
          <Link to="/timesheets" search={{ period_start: ts.pay_period.start_date }}>
            <Button variant="primary" size="sm">
              {ctaLabel}
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty card (no timesheet for a period)
// ---------------------------------------------------------------------------

function EmptyTimesheetCard({ period }: { period: DashboardPayPeriod }) {
  const stateLabel = periodStateLabel(period.state)

  return (
    <div className={styles.emptyCard}>
      <div className={styles.cardLeft}>
        <div className={styles.cardPeriodRow}>
          <span className={styles.cardPeriod}>
            {formatPeriod(period.start_date, period.end_date)}
          </span>
          {stateLabel && (
            <span className={periodStateClassName(period.state)}>{stateLabel}</span>
          )}
        </div>
        <span className={styles.emptyText}>No timesheet started</span>
      </div>
      <Link to="/timesheets" search={{ period_start: period.start_date }}>
        <Button variant="outline" size="sm">
          Start Timesheet
        </Button>
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function Dashboard() {
  const { user } = useAuth()
  const { data, isLoading, error, refetch } = useDashboardSummary()
  const { data: notifications } = useNotifications()

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading dashboard...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBox}>
          <span>Failed to load dashboard: {error.message}</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (!data) return null

  // Match timesheets to periods
  const previousTs = data.timesheets.find(
    (ts) => ts.pay_period.start_date === data.previous_period.start_date,
  )
  const currentTs = data.timesheets.find(
    (ts) => ts.pay_period.start_date === data.current_period.start_date,
  )
  return (
    <div className={styles.page}>
      <h1 className={styles.greeting}>
        Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
      </h1>

      {/* Timesheet Status */}
      <h2 className={styles.sectionTitle}>Timesheets</h2>
      <p className={styles.sectionSubtitle}>Previous and current pay periods</p>
      <div className={styles.cardList}>
        {previousTs ? (
          <TimesheetCard ts={previousTs} periodState={data.previous_period.state} />
        ) : (
          <EmptyTimesheetCard period={data.previous_period} />
        )}
        {currentTs ? (
          <TimesheetCard ts={currentTs} periodState={data.current_period.state} />
        ) : (
          <EmptyTimesheetCard period={data.current_period} />
        )}
      </div>

      {/* Schedule Context */}
      <div className={styles.scheduleSection}>
        <h2 className={styles.sectionTitle}>Upcoming schedule</h2>

        {/* Holidays */}
        {data.holidays.length > 0 && (
          <>
            <p className={styles.sectionSubtitle}>Company Holidays</p>
            <ul className={styles.scheduleList}>
              {data.holidays.map((h) => (
                <li key={h.date} className={styles.scheduleItem}>
                  <span className={styles.scheduleDate}>{formatDate(h.date)}</span>
                  <span className={styles.scheduleLabel}>{h.name}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Time Off */}
        {data.time_off.length > 0 && (
          <>
            <p className={styles.sectionSubtitle} style={{ marginTop: data.holidays.length > 0 ? 16 : 0 }}>
              Your Time Off
            </p>
            <ul className={styles.scheduleList}>
              {data.time_off.map((to, i) => (
                <li key={i} className={styles.scheduleItem}>
                  <span className={styles.scheduleDate}>
                    {formatDate(to.start_date)}
                    {to.start_date !== to.end_date && ` – ${formatDate(to.end_date)}`}
                  </span>
                  <span className={styles.scheduleType}>{to.type}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {data.holidays.length === 0 && data.time_off.length === 0 && (
          <p className={styles.emptySchedule}>No holidays or time off in the upcoming pay periods.</p>
        )}
      </div>

      {/* Notifications */}
      <NotificationSection notifications={notifications ?? []} />
    </div>
  )
}
